import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Drawer } from "@itandi/itandi-bb-ui/Drawer";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputDateRangev2 } from "@itandi/itandi-bb-ui/InputDateRangev2";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { ChangeEvent, ComponentProps } from "react";
import { useCallback, useState } from "react";
import type { CompanyDealTodoSearchCondition } from "~/models/CompanyDealTodo";
import {
	isQueryCompleted,
	selectedStaffOption,
	staffOptions
} from "~/models/CompanyDealTodo";
import type { CompanyStoreSetting } from "~/models/CompanyStoreSetting";
import type { CompanyVendor } from "~/models/CompanyVendor";
import { convertToComboBoxOption } from "~/models/CompanyVendor";
import type { DealType } from "~/models/Deal";
import { DEAL_TYPE_LABEL, DEAL_TYPES } from "~/models/Deal";
import { STATUS_OPTIONS } from "~/models/DealTodo";
import type { ItandiAccount } from "~/models/ItandiAccount";
import type { Staff } from "~/models/Staff";
import { formatHyphenDate, toDate } from "~/utils/DateUtil";

type Props = {
	account: ItandiAccount;
	companyStoreSettings: CompanyStoreSetting[];
	companyVendors: CompanyVendor[];
	onClose: () => void;
	onSubmit: (condition: CompanyDealTodoSearchCondition) => void;
	searchCondition: CompanyDealTodoSearchCondition;
	staffs: Staff[];
};

export const SearchDrawer: React.FC<Props> = ({
	account,
	companyStoreSettings,
	staffs,
	companyVendors,
	searchCondition,
	onClose,
	onSubmit
}) => {
	const [condition, setCondition] =
		useState<CompanyDealTodoSearchCondition>(searchCondition);
	const selectedStore =
		companyStoreSettings.find((store) => store.authId === condition.authId) ??
		null;
	const selectedStaff =
		staffs.find(({ id }) => id === condition.staffId) ?? null;
	const selectedCompanyVendor = companyVendors.find(
		({ id }) => id === condition.companyVendorId
	);

	const dealTypes = condition.dealTypes ?? [];

	const onCompletedChange = (e: ChangeEvent<HTMLSelectElement>): void => {
		const { value } = e.target;
		if (isQueryCompleted(value)) {
			setCondition((prev) => ({ ...prev, completed: value }));
		}
	};

	const onTextChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const { value } = e.target;
		setCondition((prev) => ({ ...prev, text: value }));
	};

	const onStoreChange: ComponentProps<typeof SingleComboBox>["onChange"] = (
		option
	) => {
		setCondition((prev) => ({
			...prev,
			authId: option && Number(option.value)
		}));
	};

	const onStaffChange: ComponentProps<typeof SingleComboBox>["onChange"] = (
		option
	) => {
		setCondition((prev) => ({
			...prev,
			staffId: option && Number(option.value)
		}));
	};

	const onDealNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const { value } = e.target;
		setCondition((prev) => ({ ...prev, dealName: value }));
	};

	const onRoomNumberChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const { value } = e.target;
		setCondition((prev) => ({ ...prev, roomNumber: value }));
	};

	const onDueDateRangeChange = ([from, to]: [
		Date | null,
		Date | null
	]): void => {
		setCondition((prev) => ({
			...prev,
			dueDateFrom: formatHyphenDate(from) ?? undefined,
			dueDateTo: formatHyphenDate(to) ?? undefined
		}));
	};

	const onDealTypeChange = useCallback((dealType: DealType) => {
		setCondition((prev) => {
			const current = prev.dealTypes ?? [];
			const index = current.indexOf(dealType);
			const newDealTypes =
				index === -1 ? [...current, dealType] : current.toSpliced(index, 1);
			return { ...prev, dealTypes: newDealTypes };
		});
	}, []);

	const onCompanyVendorChange: ComponentProps<
		typeof SingleComboBox
	>["onChange"] = (option) => {
		setCondition((prev) => ({
			...prev,
			companyVendorId: option && Number(option.value)
		}));
	};

	const onClick = (): void => {
		onSubmit({ ...condition });
		onClose();
	};

	return (
		<Drawer
			bottomArea={
				<Button onClick={onClick} variant="primary">
					検索
				</Button>
			}
			isOpen
			onClickBackdrop={onClose}
			onClickCloseButton={onClose}
			title="詳細検索"
		>
			<FormGroup variant="vertical">
				<FormControl label="ステータス">
					<InputSelect
						onChange={onCompletedChange}
						value={condition.completed}
						width="100%"
					>
						{STATUS_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</InputSelect>
				</FormControl>
				<FormControl label="タイトル・メモ">
					<Input
						onChange={onTextChange}
						placeholder="オーナーへの一時報告"
						value={condition.text}
					/>
				</FormControl>
				{account.store?.masterAccount ? (
					<FormControl label="店舗">
						<SingleComboBox
							onChange={onStoreChange}
							options={companyStoreSettings.map((storeSetting) => ({
								label: storeSetting.name,
								value: String(storeSetting.authId)
							}))}
							placeholder="店舗を選択"
							value={
								selectedStore
									? {
											label: selectedStore.name,
											value: String(selectedStore.authId)
										}
									: null
							}
						/>
					</FormControl>
				) : null}
				<FormControl label="担当者名">
					<SingleComboBox
						onChange={onStaffChange}
						options={staffOptions(staffs, account.staff)}
						placeholder="担当者を選択"
						value={selectedStaffOption(condition, selectedStaff)}
					/>
				</FormControl>
				<FormControl label="案件タイプ">
					<Flex gap={designTokens["spacing/m"]}>
						{DEAL_TYPES.map((dealType) => (
							<InputCheckBox
								checked={dealTypes.includes(dealType)}
								key={dealType}
								label={DEAL_TYPE_LABEL[dealType]}
								onChange={() => {
									onDealTypeChange(dealType);
								}}
							/>
						))}
					</Flex>
				</FormControl>
				<FormControl label="物件名">
					<Input
						onChange={onDealNameChange}
						placeholder="六本木マンション"
						value={condition.dealName}
					/>
				</FormControl>
				<FormControl label="部屋番号">
					<Input
						onChange={onRoomNumberChange}
						placeholder="101"
						value={condition.roomNumber}
					/>
				</FormControl>
				<FormControl label="案件依頼先名">
					<SingleComboBox
						onChange={onCompanyVendorChange}
						options={companyVendors.map(convertToComboBoxOption)}
						placeholder="案件依頼先を選択"
						value={
							selectedCompanyVendor
								? convertToComboBoxOption(selectedCompanyVendor)
								: null
						}
					/>
				</FormControl>
				<FormControl label="対応期限">
					<InputDateRangev2
						fromDate={toDate(condition.dueDateFrom ?? null)}
						onChange={onDueDateRangeChange}
						toDate={toDate(condition.dueDateTo ?? null)}
					/>
				</FormControl>
			</FormGroup>
		</Drawer>
	);
};
